import * as THREE from "three";
import {
  OUTLET_BENCH,
  STAND_ZONE_FORWARD,
} from "./workshopLayout";

/**
 * Human-scale outlet wiring training station (non-energized mockup).
 *
 * Keeps the named group anchors expected by foundation / hands:
 * workbench, electricalBox, receptacle, wires, terminals.
 *
 * World pose of `workbench` follows {@link OUTLET_BENCH} (left-wall repair bay).
 * Relative layout under workbench is unchanged for grab/snap contracts.
 *
 * Tagging for hands / snap (stable contract):
 * - userData.kind = 'wire' | 'terminal' | 'receptacle' | 'box'
 * - userData.wireId / terminalId = 'hot' | 'neutral' | 'ground'
 * - userData.acceptsWire, placementTarget, grabable as appropriate
 */

export type CircuitRole = "hot" | "neutral" | "ground";

export interface TrainerSceneRoots {
  root: THREE.Group;
  workbench: THREE.Group;
  electricalBox: THREE.Group;
  receptacle: THREE.Group;
  wires: THREE.Group;
  terminals: THREE.Group;
}

const ROLE_META: Record<
  CircuitRole,
  { label: string; short: string; wireColor: number; tipColor: number; terminalColor: number }
> = {
  hot: {
    label: "HOT",
    short: "H",
    wireColor: 0x1c1c1e,
    tipColor: 0xc45c26,
    terminalColor: 0xb08d57,
  },
  neutral: {
    label: "NEUTRAL",
    short: "N",
    wireColor: 0xe8e8ec,
    tipColor: 0x9aa0a8,
    terminalColor: 0xc5c9d0,
  },
  ground: {
    label: "GROUND",
    short: "GND",
    wireColor: 0x2f8f4e,
    tipColor: 0xc4a35a,
    terminalColor: 0x3d9b5c,
  },
};

/** Shared materials — keep draw calls / uniqueness low for Quest. */
function createSharedMaterials() {
  return {
    boxSteel: new THREE.MeshStandardMaterial({
      color: 0x5a6270,
      roughness: 0.55,
      metalness: 0.55,
    }),
    boxInterior: new THREE.MeshStandardMaterial({
      color: 0x3a404c,
      roughness: 0.85,
      metalness: 0.15,
    }),
    screw: new THREE.MeshStandardMaterial({
      color: 0x8a9098,
      roughness: 0.4,
      metalness: 0.7,
    }),
    receptacleBody: new THREE.MeshStandardMaterial({
      color: 0xd8dbe2,
      roughness: 0.55,
      metalness: 0.05,
    }),
    receptacleFace: new THREE.MeshStandardMaterial({
      color: 0xeef0f4,
      roughness: 0.45,
      metalness: 0.0,
    }),
    slot: new THREE.MeshStandardMaterial({
      color: 0x1a1d24,
      roughness: 0.9,
      metalness: 0.0,
    }),
    matteBlack: new THREE.MeshStandardMaterial({
      color: 0x22262e,
      roughness: 0.9,
      metalness: 0.05,
    }),
    standMarker: new THREE.MeshStandardMaterial({
      color: 0x3d4a5c,
      roughness: 0.95,
      metalness: 0.0,
      transparent: true,
      opacity: 0.85,
    }),
  };
}

type SharedMats = ReturnType<typeof createSharedMaterials>;

export function buildTrainingStation(scene: THREE.Scene): TrainerSceneRoots {
  const mats = createSharedMaterials();

  const root = new THREE.Group();
  root.name = "trainer-scene";

  const workbench = new THREE.Group();
  workbench.name = "workbench";
  // Align with the left-wall repair bay (workshopLayout / room.ts).
  workbench.position.set(OUTLET_BENCH.x, OUTLET_BENCH.y, OUTLET_BENCH.z);
  workbench.rotation.y = OUTLET_BENCH.yaw;

  const electricalBox = buildElectricalBox(mats);
  electricalBox.position.set(-0.28, 0.09, -0.02);

  const terminals = buildTerminals(mats);
  // Local to box center so snap anchors stay with the box.
  terminals.position.copy(electricalBox.position);

  const wires = buildWires(mats);
  // Laid out on the right apron — clear grab lane, not blocking the box.
  wires.position.set(0.18, 0.03, 0.14);

  const receptacle = buildReceptacle(mats);
  // Separate grab-ready mockup awaiting mount after wires.
  receptacle.position.set(0.55, 0.08, 0.02);

  const standZone = buildStandZone(mats);
  // Cue in the aisle, in front of the wall-mounted repair bay.
  const yaw = OUTLET_BENCH.yaw;
  standZone.position.set(
    OUTLET_BENCH.x + Math.sin(yaw) * STAND_ZONE_FORWARD,
    0.005,
    OUTLET_BENCH.z + Math.cos(yaw) * STAND_ZONE_FORWARD,
  );

  workbench.add(electricalBox, terminals, wires, receptacle);
  root.add(workbench, standZone);
  scene.add(root);

  return { root, workbench, electricalBox, receptacle, wires, terminals };
}

/** Open-front single-gang style training box on the bench. */
function buildElectricalBox(mats: SharedMats): THREE.Group {
  const box = new THREE.Group();
  box.name = "electrical-box";
  box.userData = {
    kind: "box",
    acceptsReceptacle: true,
    placementTarget: true,
  };

  const outerW = 0.12;
  const outerH = 0.16;
  const outerD = 0.07;
  const wall = 0.008;

  // Back plate
  const back = new THREE.Mesh(
    new THREE.BoxGeometry(outerW, outerH, wall),
    mats.boxSteel,
  );
  back.position.z = -outerD / 2 + wall / 2;
  back.castShadow = true;
  back.receiveShadow = true;
  back.name = "electrical-box-back";
  box.add(back);

  // Interior fill (slightly recessed visual volume)
  const interior = new THREE.Mesh(
    new THREE.BoxGeometry(outerW - wall * 2, outerH - wall * 2, outerD - wall),
    mats.boxInterior,
  );
  interior.position.z = -wall * 0.25;
  interior.receiveShadow = true;
  interior.name = "electrical-box-interior";
  box.add(interior);

  // Side / top / bottom rims (open toward +Z / learner)
  const rimZ = 0;
  const left = new THREE.Mesh(
    new THREE.BoxGeometry(wall, outerH, outerD),
    mats.boxSteel,
  );
  left.position.set(-outerW / 2 + wall / 2, 0, rimZ);
  left.castShadow = true;
  box.add(left);

  const right = left.clone();
  right.position.x = outerW / 2 - wall / 2;
  box.add(right);

  const top = new THREE.Mesh(
    new THREE.BoxGeometry(outerW, wall, outerD),
    mats.boxSteel,
  );
  top.position.set(0, outerH / 2 - wall / 2, rimZ);
  top.castShadow = true;
  box.add(top);

  const bottom = top.clone();
  bottom.position.y = -outerH / 2 + wall / 2;
  box.add(bottom);

  // Mount ears (top/bottom) — readable silhouette of a real box
  const earGeo = new THREE.BoxGeometry(0.045, 0.012, 0.01);
  const earTop = new THREE.Mesh(earGeo, mats.boxSteel);
  earTop.position.set(0, outerH / 2 + 0.008, outerD / 2 - 0.012);
  box.add(earTop);
  const earBot = earTop.clone();
  earBot.position.y = -outerH / 2 - 0.008;
  box.add(earBot);

  const earScrewTop = new THREE.Mesh(
    new THREE.CylinderGeometry(0.004, 0.004, 0.006, 8),
    mats.screw,
  );
  earScrewTop.rotation.x = Math.PI / 2;
  earScrewTop.position.copy(earTop.position);
  earScrewTop.position.z += 0.006;
  box.add(earScrewTop);
  const earScrewBot = earScrewTop.clone();
  earScrewBot.position.y = earBot.position.y;
  box.add(earScrewBot);

  const title = makeTextLabel("BOX", {
    width: 0.08,
    height: 0.02,
    fontSize: 28,
    fill: "#d7dde8",
    bg: "rgba(20,24,32,0.75)",
  });
  title.position.set(0, outerH / 2 + 0.028, outerD / 2 - 0.005);
  box.add(title);

  return box;
}

/**
 * Three spatially distinct terminals inside the box footprint.
 * Layout (facing learner): HOT left, NEUTRAL right, GROUND bottom-center.
 */
function buildTerminals(mats: SharedMats): THREE.Group {
  const terminals = new THREE.Group();
  terminals.name = "terminals";

  const specs: Array<{
    id: CircuitRole;
    pos: THREE.Vector3;
  }> = [
    { id: "hot", pos: new THREE.Vector3(-0.028, 0.03, 0.018) },
    { id: "neutral", pos: new THREE.Vector3(0.028, 0.03, 0.018) },
    { id: "ground", pos: new THREE.Vector3(0, -0.04, 0.018) },
  ];

  for (const spec of specs) {
    const meta = ROLE_META[spec.id];
    const terminal = new THREE.Group();
    terminal.name = `terminal-${spec.id}`;
    terminal.position.copy(spec.pos);
    terminal.userData = {
      kind: "terminal",
      terminalId: spec.id,
      acceptsWire: spec.id,
      placementTarget: true,
    };

    const postMat = new THREE.MeshStandardMaterial({
      color: meta.terminalColor,
      roughness: 0.35,
      metalness: 0.75,
    });

    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.008, 0.008, 0.016, 10),
      postMat,
    );
    post.name = `terminal-${spec.id}-post`;
    post.castShadow = true;
    terminal.add(post);

    // Snap volume hint (slightly larger, translucent ring)
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.014, 0.0025, 6, 16),
      new THREE.MeshStandardMaterial({
        color: meta.terminalColor,
        roughness: 0.5,
        metalness: 0.4,
        emissive: meta.terminalColor,
        emissiveIntensity: 0.12,
      }),
    );
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 0.002;
    ring.name = `terminal-${spec.id}-ring`;
    terminal.add(ring);

    const screw = new THREE.Mesh(
      new THREE.CylinderGeometry(0.0045, 0.0045, 0.006, 8),
      mats.screw,
    );
    screw.position.y = 0.01;
    screw.name = `terminal-${spec.id}-screw`;
    terminal.add(screw);

    const label = makeTextLabel(meta.label, {
      width: 0.05,
      height: 0.016,
      fontSize: 26,
      fill: "#f4f6fa",
      bg: roleLabelBg(spec.id),
    });
    label.position.set(0, spec.id === "ground" ? -0.022 : 0.022, 0.01);
    terminal.add(label);

    terminals.add(terminal);
  }

  return terminals;
}

/** Semi-rigid training wires — cylinders with tip caps + color + text markers. */
function buildWires(_mats: SharedMats): THREE.Group {
  const wires = new THREE.Group();
  wires.name = "wires";

  const order: CircuitRole[] = ["hot", "neutral", "ground"];
  const length = 0.26;
  const radius = 0.007;

  order.forEach((id, i) => {
    const meta = ROLE_META[id];
    const wire = new THREE.Group();
    wire.name = `wire-${id}`;
    // Spread along X; slight Z stagger so they don't stack visually.
    wire.position.set(0, 0.01 + i * 0.028, i * 0.01);
    wire.userData = {
      kind: "wire",
      wireId: id,
      grabable: true,
      role: id,
    };

    const bodyMat = new THREE.MeshStandardMaterial({
      color: meta.wireColor,
      roughness: 0.75,
      metalness: id === "neutral" ? 0.05 : 0.15,
    });
    const body = new THREE.Mesh(
      new THREE.CylinderGeometry(radius, radius, length, 10),
      bodyMat,
    );
    body.rotation.z = Math.PI / 2;
    body.castShadow = true;
    body.name = `wire-${id}-body`;
    wire.add(body);

    // Conductor tip (exposed training end) toward the box (-X)
    const tipMat = new THREE.MeshStandardMaterial({
      color: meta.tipColor,
      roughness: 0.35,
      metalness: 0.85,
    });
    const tip = new THREE.Mesh(
      new THREE.CylinderGeometry(radius * 0.85, radius * 0.85, 0.028, 8),
      tipMat,
    );
    tip.rotation.z = Math.PI / 2;
    tip.position.x = -length / 2 - 0.01;
    tip.castShadow = true;
    tip.name = `wire-${id}-tip`;
    wire.add(tip);

    // Color band near tip for redundant ID at Quest distance
    const band = new THREE.Mesh(
      new THREE.CylinderGeometry(radius * 1.15, radius * 1.15, 0.018, 8),
      new THREE.MeshStandardMaterial({
        color: meta.terminalColor,
        roughness: 0.5,
        metalness: 0.2,
        emissive: meta.terminalColor,
        emissiveIntensity: 0.08,
      }),
    );
    band.rotation.z = Math.PI / 2;
    band.position.x = -length / 2 + 0.04;
    band.name = `wire-${id}-band`;
    wire.add(band);

    const label = makeTextLabel(meta.label, {
      width: 0.07,
      height: 0.02,
      fontSize: 28,
      fill: id === "neutral" ? "#1a1d24" : "#f4f6fa",
      bg: roleLabelBg(id),
    });
    label.position.set(0.02, radius + 0.012, 0);
    // Billboard-ish: face +Z (learner)
    wire.add(label);

    // End grip nub (opposite tip)
    const nub = new THREE.Mesh(
      new THREE.SphereGeometry(radius * 1.35, 8, 8),
      bodyMat,
    );
    nub.position.x = length / 2;
    nub.name = `wire-${id}-nub`;
    wire.add(nub);

    wires.add(wire);
  });

  return wires;
}

/** Duplex-style training receptacle — separate grab-ready object. */
function buildReceptacle(mats: SharedMats): THREE.Group {
  const receptacle = new THREE.Group();
  receptacle.name = "receptacle";
  receptacle.userData = {
    kind: "receptacle",
    grabable: true,
    mountsTo: "box",
    placementTarget: "electrical-box",
  };

  const body = new THREE.Mesh(
    new THREE.BoxGeometry(0.07, 0.115, 0.028),
    mats.receptacleBody,
  );
  body.castShadow = true;
  body.receiveShadow = true;
  body.name = "receptacle-body";
  receptacle.add(body);

  const face = new THREE.Mesh(
    new THREE.BoxGeometry(0.062, 0.105, 0.006),
    mats.receptacleFace,
  );
  face.position.z = 0.017;
  face.name = "receptacle-face";
  receptacle.add(face);

  // Two outlet faces (upper / lower)
  addDuplexSlots(receptacle, mats, 0.028);
  addDuplexSlots(receptacle, mats, -0.028);

  // Center mounting screw holes
  for (const y of [0.048, -0.048, 0]) {
    const hole = new THREE.Mesh(
      new THREE.CylinderGeometry(0.0035, 0.0035, 0.008, 8),
      mats.screw,
    );
    hole.rotation.x = Math.PI / 2;
    hole.position.set(0, y, 0.02);
    receptacle.add(hole);
  }

  const label = makeTextLabel("RECEPTACLE", {
    width: 0.09,
    height: 0.02,
    fontSize: 24,
    fill: "#1a1d24",
    bg: "rgba(236,238,244,0.92)",
  });
  label.position.set(0, 0.072, 0.02);
  receptacle.add(label);

  return receptacle;
}

function addDuplexSlots(
  parent: THREE.Group,
  mats: SharedMats,
  y: number,
): void {
  const z = 0.021;
  // Neutral (longer) slot
  const neutral = new THREE.Mesh(
    new THREE.BoxGeometry(0.008, 0.022, 0.004),
    mats.slot,
  );
  neutral.position.set(-0.01, y, z);
  parent.add(neutral);

  // Hot (shorter) slot
  const hot = new THREE.Mesh(
    new THREE.BoxGeometry(0.008, 0.014, 0.004),
    mats.slot,
  );
  hot.position.set(0.01, y, z);
  parent.add(hot);

  // Ground hole
  const gnd = new THREE.Mesh(
    new THREE.CylinderGeometry(0.004, 0.004, 0.004, 8),
    mats.slot,
  );
  gnd.rotation.x = Math.PI / 2;
  gnd.position.set(0, y - 0.016, z);
  parent.add(gnd);
}

/** Floor marker: clear working stance in front of the bench. */
function buildStandZone(mats: SharedMats): THREE.Group {
  const zone = new THREE.Group();
  zone.name = "stand-zone";

  const pad = new THREE.Mesh(
    new THREE.CircleGeometry(0.38, 24),
    mats.standMarker,
  );
  pad.rotation.x = -Math.PI / 2;
  pad.receiveShadow = true;
  pad.name = "stand-zone-pad";
  zone.add(pad);

  const ring = new THREE.Mesh(
    new THREE.RingGeometry(0.36, 0.4, 32),
    new THREE.MeshBasicMaterial({
      color: 0x66a3ff,
      transparent: true,
      opacity: 0.55,
      side: THREE.DoubleSide,
    }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.002;
  zone.add(ring);

  const label = makeTextLabel("STAND HERE", {
    width: 0.42,
    height: 0.08,
    fontSize: 36,
    fill: "#d7e6ff",
    bg: "rgba(20,28,40,0.55)",
  });
  label.rotation.x = -Math.PI / 2;
  label.position.y = 0.004;
  zone.add(label);

  return zone;
}

function roleLabelBg(role: CircuitRole): string {
  switch (role) {
    case "hot":
      return "rgba(40,18,12,0.88)";
    case "neutral":
      return "rgba(220,222,228,0.92)";
    case "ground":
      return "rgba(12,40,22,0.88)";
  }
}

function makeTextLabel(
  text: string,
  opts: {
    width: number;
    height: number;
    fontSize: number;
    fill: string;
    bg: string;
  },
): THREE.Mesh {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = opts.bg;
  roundRect(ctx, 4, 8, canvas.width - 8, canvas.height - 16, 10);
  ctx.fill();
  ctx.fillStyle = opts.fill;
  ctx.font = `700 ${opts.fontSize}px system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, canvas.width / 2, canvas.height / 2 + 1);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 2;

  return new THREE.Mesh(
    new THREE.PlaneGeometry(opts.width, opts.height),
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
    }),
  );
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/** @deprecated Use {@link buildTrainingStation}. Kept as a thin alias. */
export function buildTrainerStubs(scene: THREE.Scene): TrainerSceneRoots {
  return buildTrainingStation(scene);
}
